/**
 * PostHog feature flags. LaunchDarkly keeps its own path in the gateway.
 *
 * Every surface reads through one `FlagReader`. Only where the answers come
 * from differs: posthog-js in the browser, a cached `/flags` response on the
 * server (./server-flags).
 */
import type { AnalyticsIdentity } from './types'

export type FlagValue = boolean | string

export interface FlagContext extends AnalyticsIdentity {
  /** Targeting attributes (plan, org, platform). */
  properties?: Record<string, unknown>
}

export interface FlagReadOptions {
  /**
   * Record `$feature_flag_called`. Defaults to true. Pass false for reads that
   * don't decide what the user sees, or the experiment counts people who never
   * reached it.
   */
  expose?: boolean
}

/** One flag as PostHog answered it; posthog-js's `FeatureFlagResult` shape. */
export interface FlagResult {
  enabled: boolean
  variant?: string
  payload?: unknown
}

/** Looks up one flag, recording its exposure when `expose` is set. `undefined` means no answer. */
export type FlagLookup = (key: string, options: { expose: boolean }) => FlagResult | undefined

/**
 * Flag reads. `fallback` is what an unknown flag, an outage, a non-sending
 * environment or not-yet-loaded flags read as, so gates pass `false`.
 */
export interface FlagReader {
  /** A multivariate flag answers with its variant name, which counts as on. */
  isEnabled(key: string, fallback: boolean, options?: FlagReadOptions): boolean
  getValue(key: string, fallback: FlagValue, options?: FlagReadOptions): FlagValue
  /** The flag's JSON payload. Nothing validates it against `T`. */
  getPayload<T>(key: string, fallback: T, options?: FlagReadOptions): T
}

/** Browser flags, which load after startup and reload on identify. */
export interface ClientFlags extends FlagReader {
  /** The lookup the reads go through, for readers layered on top (`useFlags`). */
  lookup: FlagLookup
  /**
   * Whether flags have loaded for the current person. False from startup and
   * from every sign-in or sign-out until that person's flags arrive; a routine
   * refresh keeps it true.
   */
  isReady(): boolean
  /** Called whenever the flag set loads or reloads. Returns an unsubscribe. */
  onChange(listener: () => void): () => void
  /**
   * Reloads the flags unless they reloaded in the last 30s. For a panel that
   * opens inside a window that never hides or gains focus.
   */
  refresh(): void
}

/** The value a result reads as: its variant, `true`, or `false`. */
export function flagValue(result: FlagResult): FlagValue {
  return result.enabled ? (result.variant ?? true) : false
}

export function createFlagReader(lookup: FlagLookup): FlagReader {
  const find = (key: string, options?: FlagReadOptions) => lookup(key, { expose: options?.expose !== false })

  return {
    isEnabled: (key, fallback, options) => find(key, options)?.enabled ?? fallback,

    getValue(key, fallback, options) {
      const result = find(key, options)
      return result ? flagValue(result) : fallback
    },

    getPayload<T>(key: string, fallback: T, options?: FlagReadOptions): T {
      // Not `??`: `null`, `false` and `0` are payloads someone saved.
      const payload = find(key, options)?.payload
      return payload === undefined ? fallback : (payload as T)
    },
  }
}

export const fallbackFlags: FlagReader = createFlagReader(() => undefined)

export const fallbackClientFlags: ClientFlags = {
  ...fallbackFlags,
  lookup: () => undefined,
  isReady: () => true,
  onChange: () => () => {},
  refresh: () => {},
}

/**
 * A lookup that answers each key with its first result until `reset`, so a
 * refresh cannot change a value the user is already looking at.
 *
 * A key first read without an exposure still records one on its first read
 * that asks for it.
 */
export function createStickyLookup(lookup: FlagLookup): { lookup: FlagLookup; reset(): void } {
  const results = new Map<string, FlagResult | undefined>()
  const exposed = new Set<string>()

  return {
    lookup(key, { expose }) {
      if (!results.has(key) || (expose && !exposed.has(key))) {
        const result = lookup(key, { expose })
        if (!results.has(key)) {
          results.set(key, result)
        }
        if (expose) {
          exposed.add(key)
        }
      }
      return results.get(key)
    },
    reset() {
      results.clear()
      exposed.clear()
    },
  }
}
